/**
 * Cookie/analytics consent — stored client-side only (localStorage), not
 * per-account. This is deliberate: `user_preferences.analytics_enabled`
 * exists in the DB schema but `update_user_profile_preferences` (the RPC
 * that persists preference changes) never writes it and `fetchPreferences`
 * never reads it back, so routing consent through that path would silently
 * lose the user's choice on the next reload. A local, always-available
 * decision is also the more conventional shape for cookie consent anyway —
 * it needs to work before a visitor has an account at all.
 */

const STORAGE_KEY = "novari_cookie_consent_v1";

export interface CookieConsent {
  analytics: boolean;
  replay: boolean;
  decidedAt: string;
}

let memory: CookieConsent | null = null;
let memoryOnly = false;

function readRaw(): CookieConsent | null {
  if (memoryOnly) return memory;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.analytics !== "boolean" ||
      typeof parsed?.decidedAt !== "string"
    ) {
      return null;
    }
    return { ...parsed, replay: parsed.analytics && parsed.replay === true };
  } catch {
    return memory;
  }
}

/** Null means no decision has been recorded yet (banner should show). */
export function getCookieConsent(): CookieConsent | null {
  return readRaw();
}

export function hasDecidedCookieConsent(): boolean {
  return readRaw() !== null;
}

export function setCookieConsent(
  analytics: boolean,
  replay = false,
): CookieConsent {
  const consent: CookieConsent = {
    analytics,
    replay: analytics && replay,
    decidedAt: new Date().toISOString(),
  };
  memory = consent;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    memoryOnly = false;
  } catch {
    memoryOnly = true;
    // Private browsing / quota exceeded — the caller's own posthog opt-in/out
    // call still applies for this page load, it just won't persist, so the
    // banner will reappear next visit. Not worth surfacing an error for.
  }
  // Lets first-run prompts (e.g. the appearance picker) wait for the banner.
  try {
    window.dispatchEvent(
      new CustomEvent("novari:cookie-consent", { detail: consent }),
    );
  } catch {
    /* non-browser */
  }
  return consent;
}
